import React, { useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Header, Table, Pagination, Button, Groupper, Message, Placeholder, useFrontier, Modal } from 'react-frontier2'
import { addCommas, bindClick, fromUnix, getNowLocal } from '@arema/components/Util'
import { SetLoading } from '@arema/components/Classes'
import { PdvDepositSource, PDVDepositType } from '../../AdminClasses'
import { useUser } from '../../AdminHooks'
import { UserLog, UserLogModal } from '../../components'
import { firstBy } from 'thenby'
import { useTranslation } from 'react-i18next'
import { PDVDeposit } from '@arema/types/internal'
import API from '../../API'
import UserAccess from "../../UserAccess";
import CreateDepositModal from './CreateDepositModal'
import { Money } from '@arema/components'

interface PDVDepositDetails{
	deposit_id: number,
	pdv_id: number,
	description: string,
	total: number,
	payout_id: number,
	source_id: number,
	source_name: string,
	deposit_type: number,
	deposit_type_name: string,
	verified: boolean,
	deleted?: boolean,
	user_creator: number,
	user_creator_name: string,
	user_verified?: number,
	user_verified_name?: string,
	user_deleted?: number,
	user_deleted_name?: string,
	date_created: number,
	date_deposit: number,
	date_verified?: number,
	date_deleted?: number,
}


interface PDVDepositListProps {
	pdv_id: number,
	onDepositsChange: ()=>void,
}

enum Modals {
	SHOW_DEPOSIT = 100,
	CONFIRM_DELETE = 200,
	VERIFY_DEPOSIT = 300,
	USERS_DEPOSIT = 400,
}

export interface DepositListElement{
	reload: ()=>void,
}

const PAGE_SIZE = 15;
var PdvDepositList = React.forwardRef((props: PDVDepositListProps, ref: React.Ref<DepositListElement>)=>{
	var { t } = useTranslation();
	var { hasAccess, user } = useUser();
	var { Toast } = useFrontier();
	var [loadError, setLoadError] = useState<string>(null);
	var [deposits, setDeposits] = useState<PDVDepositDetails[]>(null);
	var [page, setPage] = useState<number>(0);
	var [selectedDeposit, setSelectedDeposit] = useState<number>(null);
	var [modal, setModal] = useState<Modals>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		if(props.pdv_id && !isNaN(props.pdv_id)){
			loadData(page)
		}
	}, [props.pdv_id])

	useImperativeHandle(ref, ()=>({
		reload: ()=>{
			setPage(0);
			loadData(0);
		}
	}), []);

	var viewing_deposit = useMemo(()=>{
		if(!selectedDeposit || !deposits) return null;
		return deposits.find(a=>a.deposit_id===selectedDeposit);
	}, [deposits, selectedDeposit]);

	var loadData = (page?: number)=>{
		setDeposits(null);
		API.getPdvConciliaDeposits(props.pdv_id, null, page * PAGE_SIZE, PAGE_SIZE).then(res=>{
			if(res.error) return setLoadError(res.message);
			setDeposits(res.data);
		}).catch(err=>{
			setLoadError('Error inesperado cargando los depositos (LCL-1)');
		})
	}

	var openDepositDetailModal = (deposit_id: number)=>{
		return ()=>{
			setSelectedDeposit(deposit_id);
			setModal(Modals.SHOW_DEPOSIT);
		}
	}
	var openDeleteDepositModal = (deposit_id: number)=>{
		return ()=>{
			setSelectedDeposit(deposit_id);
			setModal(Modals.CONFIRM_DELETE);
		}
	}

	var openVerifyDepositModal = (deposit_id: number)=>{
		return ()=>{
			setSelectedDeposit(deposit_id);
			setModal(Modals.VERIFY_DEPOSIT);
		}
	}

	var openDepositUserModal = (deposit_id: number)=>{
		return ()=>{
			setSelectedDeposit(deposit_id);
			setModal(Modals.USERS_DEPOSIT);
		}
	}

	var closeModal = ()=>{
		setModal(null);
		setSelectedDeposit(null);
		setModalPrompts([]);
	}

	const onDeleteDeposit = (setLoading: SetLoading)=>{
		if(!selectedDeposit) return;
		setLoading(true);
		API.deletePdvDeposit(viewing_deposit.deposit_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(!res.data) return setModalPrompts(['Hubo un error inesperado eliminando el deposito. (LCL-2)']);
			var newDeposits = [...deposits];
			newDeposits = newDeposits.filter(s=>s.deposit_id !== viewing_deposit.deposit_id);
			setDeposits(newDeposits);
			Toast.success(`Se ha eliminado el deposito #${viewing_deposit.deposit_id}`);
			props.onDepositsChange();
			setSelectedDeposit(null);
			setModal(null);
			setModalPrompts([]);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado eliminando el deposito. (LCL-10)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	const onVerifyDeposit = (setLoading: SetLoading)=>{
		if(!selectedDeposit) return;
		setLoading(true);
		API.verifyPdvDeposit(viewing_deposit.deposit_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(!res.data) return setModalPrompts(['Hubo un error inesperado verificando el deposito. (LCL-2)']);
			var nd = [...deposits];
			var ix = nd.findIndex(a=>a.deposit_id===viewing_deposit.deposit_id);
			if(ix!==-1){
				nd[ix].verified = true;
				nd[ix].user_verified = user.user_id;
				nd[ix].user_verified_name = user.name;
				nd[ix].date_verified = getNowLocal().toSeconds();
			}
			setDeposits(nd);
			Toast.success(`Se ha verificado el deposito #${viewing_deposit.deposit_id}`);
			setSelectedDeposit(null);
			setModal(null);
			setModalPrompts([]);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado verificando el deposito. (LCL-10)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var onDepositSaved = (deposit: PDVDeposit, source: PdvDepositSource, deposit_type: PDVDepositType)=>{
		var d = [...deposits];
		var deposit_total = deposit.cortes ? deposit.cortes.reduce((a,b)=>a+b.amount, 0) : 0;
		var ix = d.findIndex(a=>a.deposit_id===deposit.deposit_id);
		if(ix==-1){
			d.push({
				deposit_id: deposit.deposit_id,
				description: deposit.description,
				payout_id: deposit.payout_id,
				pdv_id: props.pdv_id,
				total: deposit_total,
				source_id: source?.source_id,
				source_name: source?.source_name,
				deposit_type: deposit_type?.type_id,
				deposit_type_name: deposit_type?.description,
				user_creator: user.user_id,
				user_creator_name: user.name,
				verified: true,
				date_deposit: deposit.date_deposit,
				date_created: getNowLocal().toSeconds(),
			})
		}else{
			d[ix].total = deposit_total;
		}
		setDeposits(d.sort(firstBy('deposit_id', 'desc')));
		props.onDepositsChange();
	}

	var onPageChange = (page: number)=>{
		setPage(page);
		loadData(page)
	}

	var hasNext = deposits?.length >= PAGE_SIZE;
	return <>
		<Table style={{ maxWidth: 800, margin: 'auto', marginBottom: 15, }}>
			<Table.Head
				title={'Depositos'} 
				titleRight={hasAccess(UserAccess.CONTABILIDAD.CREATE_PDV_DEPOSIT) ? (
					<Button text="Agregar" onClick={openDepositDetailModal(null)} size="tiny" iconName="plus-circle" color="black" />
				) : null}
				data={[['ID', 'Tipo', 'Fecha', 'Informacion', 'Cantidad', '']]}
				collapsingIndexes={[0, 2, 4, 5]}
			/>
			<Table.Body>
				{deposits && deposits.length===0 && (
					<Table.Cell empty row colSpan={100} value={'No hay historial de depositos en este PDV'} />
				)}
				{deposits ? (
					deposits.map(d=>(
						<Table.Row
							key={d.deposit_id}
							collapsingIndexes={[0, 1, 2, 4, 5]}
							data={[
								d.deposit_id,
								d.source_name || t(`pdv_deposit_type.type_${d.deposit_type}`),
								fromUnix(d.date_deposit).toFormat('dd/MM/yy'),
								d.description,
								<Money value={d.total} />,
								<Button icon iconName="ellipsis-h" size="tiny" menu={[
									{ text: 'Verificar', onClick: openVerifyDepositModal(d.deposit_id), if: (hasAccess(UserAccess.CONTABILIDAD.VERIFY_PDV_DEPOSIT) && !d.verified) },
									{ text: 'Ver detalles', onClick: openDepositDetailModal(d.deposit_id) },
									{ text: 'Usuarios', onClick: openDepositUserModal(d.deposit_id) },
									{ text: 'Eliminar', disabled: !hasAccess(UserAccess.CONTABILIDAD.CANCEL_PDV_DEPOSIT), onClick: openDeleteDepositModal(d.deposit_id) }
								]} />,
							]}
						/>
					))
				) : new Array(5).fill('A').map((a, i)=>(
					<Table.Row key={`dp-${i}`} collapsingIndexes={[0, 1, 2, 4, 5]} data={[
						null,
						<Placeholder width={80} />,
						<Placeholder width={80} />,
						<Placeholder width={100} />,
						<Placeholder width={50} />,
						null,
					]} />
				))}
			</Table.Body>
			{!!deposits && (
				<Table.Footer actions={(
					<Pagination inline page={page} onPageChange={onPageChange} hasNext={hasNext} />
				)} />
			)}
		</Table>

		<Modal open={modal === Modals.CONFIRM_DELETE} onClose={closeModal} size="tiny">
			<Modal.Header>Eliminar deposito</Modal.Header>
			<Modal.Content>
				<Header text="Eliminar deposito" subtext="¿Seguro que quiere eliminarlo?" />
				<Table details style={{ marginTop: 15 }}>
					<Table.Head title="Deposito" titleSize="small" />
					<Table.Body data={[
						['ID', viewing_deposit?.deposit_id],
						['Información', viewing_deposit?.description],
					]} />
				</Table>
				{!!viewing_deposit?.payout_id && (
					<Message type='info' style={{ marginTop: 15 }} centered>
						Este deposito se creó con liquidación, lo cual hará que se elimine la liquidación <b>#{viewing_deposit.payout_id}</b>
					</Message>
				)}
				<Message list={modalPrompts} type="error" />
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={closeModal} />
				<Button text="Eliminar" color="red" onClick={onDeleteDeposit} />
			</Modal.Actions>
		</Modal>

		<Modal open={modal === Modals.VERIFY_DEPOSIT} onClose={closeModal} size="mini">
			<Modal.Header>Verificar deposito</Modal.Header>
			<Modal.Content>
				<Header text="Verificar deposito" subtext="¿Seguro que quiere marcar este deposito como verificado?" />
				<Table details style={{ marginTop: 15 }}>
					<Table.Head title="Deposito" titleSize="small" />
					<Table.Body data={[
						['ID', viewing_deposit?.deposit_id],
						['Información', viewing_deposit?.description],
					]} />
				</Table>
				<Message list={modalPrompts} type="error" />
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cerrar" basic onClick={closeModal} />
				<Button text="Verificar" color="black" onClick={onVerifyDeposit} />
			</Modal.Actions>
		</Modal>

		<UserLogModal open={modal === Modals.USERS_DEPOSIT} onClose={closeModal} user={viewing_deposit} />
		<CreateDepositModal
			open={modal === Modals.SHOW_DEPOSIT}
			onClose={bindClick(setModal)}
			pdvId={props.pdv_id}
			onSave={onDepositSaved}
			depositId={viewing_deposit?.deposit_id}
		/>
	</>
});

export default PdvDepositList;
